/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ConstructionSiteDocumentService } from '@construction-sites/services/construction-site-document.service';
import * as ConstructionSiteActions from '@construction-sites/store/construction-sites.actions';
import {
  getConstructionSiteActionPending,
  getCurrentConstructionSite
} from '@construction-sites/store/construction-sites.selectors';
import { State } from '@core/store';
import { Store } from '@ngrx/store';
import { ConstructionSite, UserRole } from '@shared/models';
import { UploadedDocument } from '@shared/models/uploaded-document.model';
import { OAuthService } from 'angular-oauth2-oidc';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { NgxPermissionsService } from 'ngx-permissions';
import { Observable, of, Subscription } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-form-documents',
    templateUrl: './form-documents.component.html',
    styleUrls: ['./form-documents.component.scss'],
    standalone: false
})
export class FormDocumentsComponent implements OnInit, OnDestroy {
  @Input() constructionSite: ConstructionSite;
  displayedColumns: string[] = ['fileName', 'isVao', 'url', 'delete'];
  canWrite = false;

  constructionSite$: Observable<ConstructionSite>;
  actionPending$: Observable<boolean>;
  filesSelected$: Observable<boolean>;
  public uploader = new FileUploader({ url: '' });
  uploadFileSubscription: Subscription;
  deleteFileSubscription: Subscription;

  constructor(
    private authService: OAuthService,
    private uploadService: ConstructionSiteDocumentService,
    private store: Store<State>,
    private permissionsService: NgxPermissionsService,
  ) {
    this.uploader.setOptions(
      {
        url: '',
        headers: [
          {name: 'Accept', value: 'application/ld+json'},
          {name: 'Authorization', value: 'Bearer ' + this.authService.getAccessToken()}
        ],
        removeAfterUpload: true
      }
    );
  }

  ngOnInit(): void {
    this.permissionsService.hasPermission(UserRole.User).then(canWrite => {
      this.canWrite = canWrite;
      if (false === canWrite) {
        this.displayedColumns = this.displayedColumns.filter(column => 'delete' !== column);
      }
    });

    this.uploader.onAfterAddingAll = () => {
      this.filesSelected$ = of(true);
    };
    this.uploader.onCompleteAll = () => {
      this.filesSelected$ = of(false);
    };

    this.actionPending$ = this.store.select(getConstructionSiteActionPending);
    this.setCurrentConstructionSite(this.constructionSite);
    this.constructionSite$ = this.store.select(getCurrentConstructionSite);
  }

  ngOnDestroy(): void {
    if (this.uploadFileSubscription) {
      this.uploadFileSubscription.unsubscribe();
    }
    if (this.deleteFileSubscription) {
      this.deleteFileSubscription.unsubscribe();
    }
  }

  onConfirm(): void {
    this.uploadFileSubscription = this.store.select(getCurrentConstructionSite).pipe(
      tap(constructionSite => this.uploader.queue.map(file => {
        const isVao = document.getElementById(this.slugify(file) + '-input') as HTMLInputElement;
        this.uploadService.uploadDocument(constructionSite['@id'], file, isVao.checked).subscribe(
          updatedConstructionSite => this.setCurrentConstructionSite(updatedConstructionSite)
        );
      }))
    ).subscribe();
  }

  cancelDefault(event: MouseEvent): void {
    event.preventDefault();
  }

  slugify(fileItem: FileItem): string {
    return fileItem.file.name.toLowerCase().replace(/\s/g, '');
  }

  deleteDocumentFromConstructionSite(document: UploadedDocument): void {
    this.deleteFileSubscription = this.store.select(getCurrentConstructionSite).pipe(
      switchMap(constructionSite => this.uploadService.deleteDocument(constructionSite, document['@id'])),
      tap(updatedConstructionSite => this.setCurrentConstructionSite(updatedConstructionSite))
    ).subscribe();
  }

  private setCurrentConstructionSite(constructionSite: ConstructionSite): void {
    this.store.dispatch(new ConstructionSiteActions.SetCurrentConstructionSite(constructionSite));
  }
}
